import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { PartnerService } from '@feature/dashboard/partners/services/partner.service';
import { PartnerListInterface } from '@feature/dashboard/partners/types/partner-list';
import { InputModule } from '@sharedModule/components/input/input.module';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { MaterialModule } from '@sharedModule/material/material.module';
import { ColumnPipe } from '@sharedModule/pipe/column/column.pipe';
import { removeEmptyStrings } from '@sharedModule/utils/form.utils';
import { Observable, Subject, takeUntil } from 'rxjs';
import { TransactionSettingsService } from '../../services/transaction-settings.service';
import { MasterPartnerTransactionSettingsInterface } from '../../types/transaction-settings';
import {
  UpsertMasterPartnerTransactionSettingsPopupInterface,
  UpsertMasterPartnerTransactionSettingsInterface,
} from '../../types/upsert-transaction-settings';

@Component({
  selector: 'app-upsert-master-partner-transaction-settings',
  standalone: true,
  imports: [
    ColumnPipe,
    InputModule,
    CommonModule,
    MaterialModule,
    ReactiveFormsModule,
  ],
  templateUrl: './upsert-master-partner-transaction-settings.component.html',
  styleUrl: './upsert-master-partner-transaction-settings.component.scss',
})
export class UpsertMasterPartnerTransactionSettingsComponent
  implements OnInit, OnDestroy
{
  public readonly attrConstant = ATTRIBUTES_CONSTANT.settings.transaction;
  upsertTransactionSettingFrom!: FormGroup;
  partnerList!: Observable<PartnerListInterface[]>;
  transactionSettings!: MasterPartnerTransactionSettingsInterface[];
  isEdit!: boolean;
  isSubmitLoading!: boolean;
  #destroy$ = new Subject<void>();

  constructor(
    private partnerService: PartnerService,
    private messageHandlerService: MessageHandlerService,
    @Inject(MAT_DIALOG_DATA)
    public txnData: UpsertMasterPartnerTransactionSettingsPopupInterface,
    public closeDialogRef: MatDialogRef<UpsertMasterPartnerTransactionSettingsComponent>,
    private transactionSettingsService: TransactionSettingsService
  ) {}

  ngOnInit(): void {
    const isUpdate = this.txnData.isUpdate;
    this.upsertTransactionSettingFrom = new FormGroup({
      [this.attrConstant.partner.key]: new FormControl(
        isUpdate ? this.txnData.partner_id : '',
        [Validators.required]
      ),
      code: new FormControl(isUpdate ? this.txnData.code : '', [
        Validators.required,
      ]),
    });
    this.partnerList = this.partnerService.sharedData$;
    this.getTransactionSettings();
  }

  getTransactionSettings(): void {
    this.transactionSettingsService
      .getTransactionSettings()
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success)
            this.transactionSettings = x.response[this.txnData.column];
        },
      });
  }

  onSubmit(): void {
    if (this.upsertTransactionSettingFrom.valid) {
      this.isSubmitLoading = true;
      const payload = {
        ...this.upsertTransactionSettingFrom.getRawValue(),
        column: this.txnData.column,
      };
      if (this.txnData.isUpdate) this.updateTransactionSetting(payload);
      else this.addTransactionSetting(payload);
    }
  }

  addTransactionSetting(
    payload: UpsertMasterPartnerTransactionSettingsInterface
  ): void {
    this.transactionSettingsService
      .addMasterPartnerTransactionSetting(removeEmptyStrings(payload))
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.messageHandlerService.show(x.message);
            this.closeDialogRef.close({
              success: true,
              partnerId: payload.partner_id,
            });
          }
        },
        error: error => {
          console.log(error);
          this.closeDialogRef.close({
            success: false,
          });
        },
      });
  }

  updateTransactionSetting(
    payload: UpsertMasterPartnerTransactionSettingsInterface
  ): void {
    this.transactionSettingsService
      .updateMasterPartnerTransactionSetting(removeEmptyStrings(payload))
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.messageHandlerService.show(x.message);
            this.closeDialogRef.close({
              success: true,
              partnerId: payload.partner_id,
            });
          }
        },
        error: error => {
          console.log(error);
          this.closeDialogRef.close({
            success: false,
          });
        },
      });
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
